import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { updateModuleProgress, completeUserModule } from '@/api/module';
import DynamicSurveyQuestion from './DynamicSurveyQuestion';
import { useSurveyQuestions } from '@/hooks/useSurveyQuestions';
import { useSurveyResponse } from '@/hooks/useSurveyResponse';
import { fetchResponses } from '@/api/survey';
import AdminJsonToggle from './AdminJsonToggle';
import type { UserModule } from '@/types/api';
import type { SurveyType } from '@/types/surveyTypes';

interface DeclarativeSurveyModuleProps {
  userModule: UserModule;
  startAt: number;
}

/**
 * Survey module that uses progressCheckpoint from backend
 * Manages navigation without URL-based state
 */
const DeclarativeSurveyModule: React.FC<DeclarativeSurveyModuleProps> = ({
  userModule,
  startAt
}) => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(startAt);
  
  // Determine survey type from module
  const surveyId = userModule.module.surveyId || 0;
  const surveyType: SurveyType = surveyId === 1 ? 'pulse' : 'shape';
  const surveyName = surveyType === 'pulse' ? 'PULSE' : 'SHAPE';
  
  // Fetch questions from backend
  const { data: questions, isLoading } = useSurveyQuestions(surveyId);
  
  // Fetch existing responses
  const { data: responses } = useQuery({
    queryKey: ['survey-responses', surveyId, userModule.id],
    queryFn: () => fetchResponses(surveyId, userModule.id),
    enabled: !!surveyId && !!userModule.id
  });
  
  // Survey response mutation
  const submitResponseMutation = useSurveyResponse();
  
  const totalQuestions = questions?.length || 0;
  const currentQuestion = questions?.[currentQuestionIndex];
  
  // Get current answer from responses
  const getCurrentAnswer = () => {
    if (!currentQuestion || !responses) return undefined;
    
    const response = responses.find((r: { questionId: number; response?: number; textResponse?: string }) => r.questionId === currentQuestion.id);
    if (!response) return undefined;
    
    // Return the appropriate response based on type
    return response.textResponse || response.response;
  };
  
  // Progress update mutation
  const progressMutation = useMutation({
    mutationFn: (checkpoint: number) => 
      updateModuleProgress(userModule.id, checkpoint),
    onError: (error) => {
      console.error('Failed to update progress:', error);
      // Don't show error toast for progress updates - they're not critical
    }
  });
  
  // Module completion mutation
  const completeMutation = useMutation({
    mutationFn: () => completeUserModule(userModule.id),
    onSuccess: (nextModule) => {
      if (nextModule) {
        // There's a next module, stay on /module
        window.location.reload(); // Reload to get fresh module data
      } else {
        // No more modules, go to dashboard
        navigate('/updates');
      }
    },
    onError: (error) => {
      console.error('Failed to complete module:', error);
    }
  });
  
  // Update progress in backend when question changes
  useEffect(() => {
    if (currentQuestionIndex !== startAt) {
      progressMutation.mutate(currentQuestionIndex);
    }
  }, [currentQuestionIndex]);
  
  const handleBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);
  
  const handleNext = useCallback(async () => {
    if (!totalQuestions) return;
    
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Survey complete
      completeMutation.mutate();
    }
  }, [currentQuestionIndex, totalQuestions, completeMutation]);
  
  // Handle answer submission - must be defined before any early returns
  const handleAnswer = useCallback(async (answer: number | string) => {
    if (!currentQuestion) return;
    
    console.log('DeclarativeSurveyModule: Submitting answer:', {
      userModuleId: userModule.id,
      questionId: currentQuestion.id,
      answer,
      answerType: typeof answer,
      questionType: currentQuestion.responseType
    });
    
    submitResponseMutation.mutate({
      userModuleId: userModule.id,
      questionId: currentQuestion.id,
      response: answer
    }, {
      onSuccess: () => {
        console.log('DeclarativeSurveyModule: Answer submitted successfully');
      },
      onError: (error) => {
        console.error('DeclarativeSurveyModule: Failed to submit answer:', error);
      }
    });
  }, [currentQuestion, userModule.id, submitResponseMutation]);
  
  if (isLoading || !currentQuestion) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading survey questions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <AdminJsonToggle 
        data={{
          module: userModule.module,
          currentQuestionIndex,
          totalQuestions,
          surveyType,
          responses: responses || []
        }}
        label="Survey Module Data"
      />
      <DynamicSurveyQuestion
        questionNumber={currentQuestionIndex + 1} // Display as 1-based
        totalQuestions={totalQuestions}
        question={currentQuestion}
        onAnswer={handleAnswer}
        currentAnswer={getCurrentAnswer()}
        onBack={handleBack}
        onNext={handleNext}
        canGoBack={currentQuestionIndex > 0}
        canGoNext={true}
        surveyName={surveyName}
        module={userModule.module}
      />
    </>
  );
};

export default DeclarativeSurveyModule;